import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LogoBanner from "@/components/LogoBanner";
import LeadFormModal from "@/components/LeadGenerationForm/LeadFormModal";
import YouTubeEmbedSection from "@/components/YouTubeEmbedSection";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useScrollTracking } from "@/hooks/useScrollTracking";
const AboutUs = () => {
  // Tracking hooks
  usePageTracking();
  useScrollTracking();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formTrigger, setFormTrigger] = useState({ location: "unknown", text: "unknown" });
  const openForm = (triggerLocation: string, triggerText: string) => {
    setFormTrigger({ location: triggerLocation, text: triggerText });
    setIsFormOpen(true);
  };
  const closeForm = () => setIsFormOpen(false);
  const isMobile = useIsMobile();
  return <>
      <Header onOpenForm={() => openForm("about-header", "Envoyer votre demande")} forceScrolled={true} />
      <LeadFormModal isOpen={isFormOpen} onClose={closeForm} triggerLocation={formTrigger.location} triggerText={formTrigger.text} />
      
      <main className="flex-grow pt-24 md:pt-28">
        {/* Section Héro */}
        <section className="py-12 md:py-20 px-4 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="space-y-6">
              <h1 className="text-4xl sf-font font-normal md:text-6xl">À propos de Naboo</h1>
              <h2 className="text-xl text-gray-600 sf-font leading-relaxed">
                Chez Naboo, nous visons à connecter les entreprises avec les meilleurs lieux et prestataires de services, 
                tout en rendant l'organisation plus fluide et plus efficace.
              </h2>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex justify-center md:justify-end">
              <img src="https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/About%20us.png" alt="L'équipe Naboo" className="max-w-full h-auto rounded-lg" />
            </motion.div>
          </div>
        </section>
        
        {/* Section de confiance avec bannière de logos */}
        <section className="pb-2 md:pb-4">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-4 sf-font">Plus de 4 000 entreprises nous confient l'organisation de leur séminaire</h3>
          
          {/* Bannière de logos dynamique sans séparateurs et titre */}
          <div className="bg-white overflow-hidden py-8">
            <LogoBanner hideTitle={true} hideSeparators={true} />
          </div>
        </section>

        {/* Section Statistiques */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {[{
              value: "4 000+",
              label: "Entreprises clientes"
            }, {
              value: "50 000+",
              label: "Partenaires à travers l'Europe"
            }, {
              value: "200 000+",
              label: "Collaborateurs partis"
            }, {
              value: "500M€",
              label: "Achats événementiels optimisés"
            }].map((stat, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} className="text-center">
                  <p className="text-4xl md:text-5xl text-black mb-2 font-normal">{stat.value}</p>
                  <p className="text-gray-600">{stat.label}</p>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Section Notre Histoire */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-normal text-center mb-12">Notre Histoire</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Naboo a été fondée en 2022 par Maxime Eduardo, Antoine Servant, Lucien Bredin et Jean-Louis, 
                deux anciens consultants d'Oliver Wyman FS et deux entrepreneurs passionnés de technologie. 
                Leur ambition ? Simplifier radicalement l'organisation des événements d'entreprise en offrant un 
                service clé en main sans frais cachés.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Ce qui a commencé comme une vision ambitieuse s'est rapidement transformé en une expérience essentielle. Aujourd'hui, 
                plus de 200 000 professionnels ont confié leurs séminaires à Naboo, faisant de nous leur choix privilégié 
                pour des événements inoubliables.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section Nos Valeurs */}
        <section className="py-16 md:py-24 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="space-y-12">
              <h2 className="text-4xl md:text-5xl font-normal text-center mb-12">Nos Valeurs</h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Innovation</h3>
                  <p className="text-lg text-gray-700">
                    Nous intégrons continuellement des technologies comme l'IA pour garantir l'efficacité, 
                    afin que nos équipes puissent se concentrer sur la relation client.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Simplicité</h3>
                  <p className="text-lg text-gray-700">
                    Notre offre est conçue pour être clé en main, avec un point de contact unique, 
                    une seule facture, et une plateforme unifiée pour tous vos prestataires.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Transparence</h3>
                  <p className="text-lg text-gray-700">
                    Nos prix sont 100% transparents sans frais cachés, pour une collaboration en toute confiance.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section Notre Mission Future */}
        <section className="py-24 md:py-32 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-normal text-center mb-12">Notre Mission Future</h2>
              <p className="text-lg text-gray-700 leading-relaxed">Après s'être imposé en France, au Royaume-Uni, en Belgique, en Italie, en Espagne, en Allemagne et aux Pays-Bas, Naboo vise désormais à conquérir le marché américain. Comme le souligne souvent Maxime, "nous avançons au rythme de nos clients, partout où ils nous poussent à ouvrir des marchés."</p>
              <br />
              <p className="text-lg text-gray-700 leading-relaxed">Nous construisons aujourd'hui l'infrastructure mondiale du procurement événementiel. Grâce à une IA propriétaire, nous transformons une catégorie de dépense complexe en un système automatisé, conforme et mesurable pour les plus grandes entreprises.</p>
              <br />
              <p className="text-lg text-gray-700 leading-relaxed">Des agents IA pour le booking instantané au sourcing optimisé, nous industrialisons le secteur pour offrir à nos clients une maîtrise totale de leurs événements, à l'échelle globale.</p>
              
            </motion.div>
          </div>
        </section>

        {/* Section Vidéo de Levée de Fonds */}
        <section className="pt-0 pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} className="text-center space-y-6 mb-12">
              <h2 className="text-4xl md:text-5xl font-normal">Naboo lève 70M$ en Série B</h2>
              <p className="text-xl text-gray-600 mx-auto whitespace-nowrap">Notre ambition : construire la plateforme mondiale d'événementiel B2B pilotée par l'IA</p>
            </motion.div>
            
            <div className="flex justify-center">
              <div className="relative w-full max-w-4xl aspect-video">
                <YouTubeEmbedSection videoId="Sljv1J8vU28" autoplay={false} controls={true} mute={false} />
              </div>
            </div>
          </div>
        </section>

        {/* Section CTA */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} className="text-center space-y-6">
              <h2 className="text-4xl md:text-5xl font-normal">Planifiez dès maintenant votre prochain évènement</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">Rejoignez des milliers d'entreprises qui font confiance à Naboo pour l'organisation de leurs séminaires. Partagez-nous vos besoins et notre équipe reviendra vers vous sous 24 heures.</p>
              <div className="pt-8">
                <Button onClick={() => openForm("about-cta", isMobile ? "Commencer" : "Commencez à planifier votre événement")} className="font-semibold px-4 py-2 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:shadow-xl hover:shadow-cta-green/30">
                  <Send className="h-4 w-4 mr-1" />
                  {isMobile ? "Commencer" : "Commencez à planifier votre événement"}
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>;
};
export default AboutUs;